import React, { useState, useEffect, useMemo } from "react"
import { Search, Check, X } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { fetchMembers } from "src/features/members/api/membersApi"

function initials(name, email) {
  const base = (name || email || "?").trim()
  const parts = base.split(/\s+/).filter(Boolean)
  return (parts.slice(0, 2).map((p) => p[0]).join("") || base[0] || "?").toUpperCase()
}

function memberLabel(m) {
  return m.user?.name || m.user?.email || `Member #${m.id}`
}

// Searchable multi-select over the current org's members. The result list only
// appears once the user types; selected members show as removable chips above.
//   selectedIds = array of membership ids
//   onChange    = next array of membership ids
export function TeamMembersTab({ selectedIds, onChange }) {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchMembers({ per_page: 100 })
      .then((d) => !cancelled && setMembers(d.memberships || []))
      .catch(() => !cancelled && setMembers([]))
      .finally(() => !cancelled && setLoading(false))
    return () => { cancelled = true }
  }, [])

  const selected = useMemo(() => new Set(selectedIds.map(String)), [selectedIds])
  const byId = useMemo(() => Object.fromEntries(members.map((m) => [String(m.id), m])), [members])
  const selectedMembers = selectedIds.map((id) => byId[String(id)]).filter(Boolean)

  function toggle(id) {
    const sid = String(id)
    const next = new Set(selected)
    next.has(sid) ? next.delete(sid) : next.add(sid)
    onChange([...next].map(Number))
  }

  const q = search.trim().toLowerCase()
  const results = useMemo(() => {
    if (!q) return []
    return members.filter((m) => {
      const u = m.user || {}
      return [u.name, u.email, u.phone].some((v) => v?.toLowerCase().includes(q))
    })
  }, [members, q])

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-slate-500">Add members from your organization.</p>
        <span className="text-[12px] font-medium text-slate-400">{selected.size} selected</span>
      </div>

      {/* Selected chips — always visible so users can review/remove. */}
      {selectedMembers.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedMembers.map((m) => (
            <span
              key={m.id}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 py-1 pl-2.5 pr-1.5 text-[12px] text-slate-700"
            >
              {memberLabel(m)}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => toggle(m.id)}
                className="flex h-4 w-4 items-center justify-center rounded-full p-0 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                aria-label={`Remove ${memberLabel(m)}`}
              >
                <X className="h-3 w-3" />
              </Button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <Search
          style={{ left: "0.75rem" }}
          className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
        />
        <Input
          style={{ paddingLeft: "2.5rem" }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={loading ? "Loading members…" : "Search members to add…"}
          disabled={loading}
        />
      </div>

      {/* Result list — only rendered while searching. */}
      {q && (
        <div className="max-h-[280px] divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
          {results.length === 0 ? (
            <p className="px-3 py-6 text-center text-[13px] text-slate-400">No members match your search.</p>
          ) : (
            results.map((m) => {
              const u = m.user || {}
              const isOn = selected.has(String(m.id))
              return (
                <Button
                  key={m.id}
                  type="button"
                  variant="ghost"
                  onClick={() => toggle(m.id)}
                  className="flex h-auto w-full items-center justify-start gap-3 rounded-none px-3 py-2.5 text-left font-normal hover:bg-slate-50"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600">
                    {initials(u.name, u.email)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-slate-900">{memberLabel(m)}</span>
                    {u.email && <span className="block truncate text-[12px] text-slate-400">{u.email}</span>}
                  </span>
                  {m.role?.name && (
                    <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
                      {m.role.name}
                    </span>
                  )}
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                      isOn ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white"
                    }`}
                  >
                    {isOn && <Check className="h-3.5 w-3.5" />}
                  </span>
                </Button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
